/** Eroded remnants of a coastal reach. These are closed radial landforms,
 * rooted in the shared seabed, not decorative rocks or overhanging meshes. */
import { hashSeed, rngFromString } from './rng.js';
import * as pmath from './pmath.js';
import { planarField } from './landforms.js';
import { coastalDirection, coastalShore, type CoastalLandform } from './coastalLandforms.js';
import type { PlanetDef } from './universe.js';

export interface SeaStack {
  readonly id: string;
  readonly u: number;
  readonly v: number;
  /** Conservative circular footprint; all fracture planes lie inside it. */
  readonly radiusM: number;
  readonly heightM: number;
  readonly tiltU: number;
  readonly tiltV: number;
  readonly seed: number;
  readonly crownDatum: number;
  /** Convex fracture planes, dot(local, normal) / offset. */
  readonly planes: readonly (readonly [number, number, number])[];
}
const ease=(x:number)=>{const t=Math.max(0,Math.min(1,x));return t*t*(3-2*t);};

/** Allocation-free polygonal crown, steep faces and a submerged erosion apron.
 * At the footprint edge the height AND first derivative join the old seabed. */
export function seaStackHeight(stack:SeaStack,u:number,v:number,seaM:number,baseM:number):number {
  const x=u-stack.u,z=v-stack.v,R=stack.radiusM;
  if(x*x+z*z>=R*R)return baseM;
  let q=0;
  for(const [nx,nz,offset] of stack.planes)q=Math.max(q,(x*nx+z*nz)/offset);
  if(q>=1)return baseM;
  // Broad physical fractures break the otherwise extruded polygon into uneven
  // brows and oblique faces. Their scale is metres, independent of mesh LOD.
  const fracture=planarField(x/(R*.38)+3.7,z/(R*.48)-2.9,.4,stack.seed);
  const crown=stack.heightM*(1+(fracture-stack.crownDatum)*.22)+x*stack.tiltU+z*stack.tiltV;
  q+=fracture*.16*ease(q/.25)*ease((1-q)/.2);
  const joint=planarField(x/(R*.55)+2.1,z/(R*.55)-1.3,(q-.40)*8.0,stack.seed^0x4a4f494e);
  q+=joint*.07*ease((q-.35)/.13)*(1-ease((q-.78)/.14));
  // The unequal plane offsets break radial symmetry. A broad tilted crown and
  // straight wall distinguish a stack from a pointed cone or rounded boulder.
  const wall=Math.max(0,Math.min(1,(q-.40)/.44));
  const cut=wall<.08?wall*wall/.1472:wall>.92?1-(1-wall)*(1-wall)/.1472:(wall-.04)/.92;
  const rock=seaM+crown*(1-cut)-2.5*cut;
  const apron=ease((1-q)/.16);
  return Math.max(baseM,baseM+(rock-baseM)*apron);
}

/** Independent salts preserve the parent reach and every existing shape RNG.
 * The complete footprint must be submerged, with a wet gap to the mainland. */
export function buildSeaStacks(f:CoastalLandform,def:PlanetDef,height:(u:number,v:number)=>number):readonly SeaStack[] {
  const stacks:SeaStack[]=[];
  if(f.kind==='shelving-beach')return Object.freeze(stacks);
  const rng=rngFromString(`${f.id}:sea-stacks:1`),scale=Math.min(1,f.inlandM/450);
  if(rng()>.76)return Object.freeze(stacks);
  const seaM=def.seaRadius!-def.radius,limit=1+Math.floor(rng()*3);
  for(let attempt=0;attempt<18&&stacks.length<limit;attempt++){
    const side=rng()<.5?-1:1;
    const u=(f.kind==='pocket-beach'?side*(.42+rng()*.25):(rng()-.5)*1.15)*f.halfLengthM;
    const radiusM=(24+rng()*29)*scale;
    const projection=f.kind==='pocket-beach'?f.projectionM*(side<0?1:.78):
      f.kind==='rocky-headland'?f.projectionM*(.3+.7*(1-Math.abs(u/f.halfLengthM))):0;
    const v=coastalShore(f,u)-projection-radiusM-(35+rng()*95)*scale;
    // Stay within the fully weighted reach, keeping existing coastal joins.
    if(Math.abs(u)+radiusM>f.halfLengthM*.8||v-radiusM < -f.offshoreM*.62)continue;
    if(stacks.some(s=>Math.hypot(s.u-u,s.v-v)<s.radiusM+radiusM+18*scale))continue;
    let valid=height(u,v)<seaM-3;
    for(let k=0;valid&&k<12;k++){
      const a=k*pmath.TWO_PI/12;
      // A water annulus rules out cliff-attached spurs and buried feet.
      valid=height(u+pmath.cos(a)*(radiusM+12*scale),v+pmath.sin(a)*(radiusM+12*scale))<seaM-1.2;
    }
    if(!valid)continue;
    const count=5+Math.floor(rng()*3),phase=rng()*pmath.TWO_PI,planes:(readonly [number,number,number])[]=[];
    for(let k=0;k<count;k++){
      const a=phase+k*pmath.TWO_PI/count;
      planes.push(Object.freeze([pmath.cos(a),pmath.sin(a),radiusM*(.60+rng()*.16)] as const));
    }
    const seed=hashSeed(`${f.id}:stack:${attempt}:fractures`);
    stacks.push(Object.freeze({id:`${f.id}:stack:${attempt}`,u,v,radiusM,seed,crownDatum:planarField(3.7,-2.9,.4,seed),
      heightM:Math.min(85*scale,f.cliffM*(stacks.length===0?.40+rng()*.28:.07+rng()*.32)),
      tiltU:(rng()-.5)*.32,tiltV:(rng()-.5)*.32,planes:Object.freeze(planes)}));
  }
  return Object.freeze(stacks);
}

export function seaStackDirection(f:CoastalLandform,stack:SeaStack,radius:number) {
  return coastalDirection(f,radius,stack.u,stack.v);
}
